// The end of a practice session: score, XP (Kids: stars and a sticker), best streak, what to review with the rule
// for each miss, drills for recurring mistakes, and what to do next.

import type { FC, ReactNode } from 'react'
import type { L } from '@/prakrit/lib/lessons/types'
import type { Level, PlayedQuestion, SessionMode, SetPlace } from '@/prakrit/components/practice/practice-types'

import Link from 'next/link'
import {
    ArrowRight,
    Award,
    Dumbbell,
    Flame,
    LayoutGrid,
    Medal,
    RotateCcw,
    Sparkles,
    Star,
    Trophy,
    TrendingUp,
    UserPlus
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { WEAKNESS } from '@/prakrit/lib/progress-shared'
import { Bi } from '@/prakrit/components/common/bi'
import { XpAccountNote } from '@/components/plans/plan-notice'
import { StatusBadge } from '@/prakrit/components/common/status-badge'
import { LEVEL_INFO, modeInfo } from '@/prakrit/components/practice/practice-catalog'
import { LEVELS } from '@/prakrit/components/practice/practice-types'
import { practiceHref, practiceHubHref } from '@/prakrit/components/practice/practice-topics'

interface SessionSummaryProps {
    mode: SessionMode
    level: Level
    played: PlayedQuestion[]
    bestStreak: number
    kids: boolean
    signedIn: boolean
    onReplay: () => void
    /** The session's topics: kept for the next level and on the way back to all games. */
    topics?: string[]
    /** विकल्प सहित: kept the same way. */
    vikalp?: boolean
    /** The session was a numbered practice set: offers the next set and the list of sets. */
    set?: SetPlace | null
}

const CHEER_KIDS: { min: number, sticker: typeof Trophy, text: L }[] = [
    { min: 0.9, sticker: Trophy, text: { hi: 'वाह! तुम तो प्राकृत के सितारे हो!', en: 'Wow, you are a Prakrit star!' } },
    { min: 0.6, sticker: Medal, text: { hi: 'बहुत अच्छे! एक बार और खेलें?', en: 'Great job! Play once more?' } },
    { min: 0, sticker: Award, text: { hi: 'शाबाश! अभ्यास से और भी अच्छा होगा।', en: 'Well done! Practice makes it even better.' } }
]

const CHEER_ADULTS: { min: number, text: L }[] = [
    { min: 0.9, text: { hi: 'उत्कृष्ट! यह स्तर आपके लिए आसान है — अगला स्तर आज़माइए।', en: 'Excellent. This level is easy for you; try the next one.' } },
    { min: 0.6, text: { hi: 'अच्छा सत्र। नीचे के नियम एक बार दोहरा लीजिए।', en: 'A good session. Go over the rules below once more.' } },
    { min: 0, text: { hi: 'शुरुआत हो गई। नीचे के नियम पढ़कर फिर से खेलिए।', en: 'A start. Read the rules below and play again.' } }
]

const Stat: FC<{ label: L, value: ReactNode, icon: ReactNode }> = ({ label, value, icon }): ReactNode => (
    <div className='flex flex-col items-center gap-1 rounded-xl border border-border bg-card px-3 py-3 shadow-card'>
        <span className='inline-flex items-center gap-1.5 text-2xl font-bold'>
            {icon}
            {value}
        </span>
        <span className='text-xs text-muted-foreground'>
            <Bi hi={label.hi} en={label.en} layout='inline' className='justify-center' always />
        </span>
    </div>
)

export const SessionSummary: FC<SessionSummaryProps> = ({ mode, level, played, bestStreak, kids, signedIn, onReplay, topics = [], vikalp = false, set = null }): ReactNode => {
    const total = played.length
    const correct = played.filter(p => p.result.correct).length
    const xp = played.reduce((sum, p) => sum + p.result.xpAwarded, 0)
    const share = total > 0 ? correct / total : 0
    const kidsCheer = CHEER_KIDS.find(c => share >= c.min) ?? CHEER_KIDS[CHEER_KIDS.length - 1]
    const adultsCheer = CHEER_ADULTS.find(c => share >= c.min) ?? CHEER_ADULTS[CHEER_ADULTS.length - 1]
    const Sticker = kidsCheer.sticker
    const misses = played.filter(p => !p.result.correct)
    const codes = Array.from(new Set(misses.flatMap(p => p.result.attempts.filter(a => !a.correct && a.code).map(a => a.code as string))))
    const drills = codes.filter(code => WEAKNESS[code]).map(code => ({ code, ...WEAKNESS[code] }))
    const nextLevel = LEVELS[LEVELS.indexOf(level) + 1]
    const hubHref = practiceHubHref(mode !== 'aaj' ? topics : [], vikalp)

    return (
        <section aria-labelledby='summary-heading' className='animate-pop-in flex flex-col gap-6'>
            <header className='flex flex-col items-center gap-3 text-center'>
                {kids ? (
                    <span className='flex size-24 items-center justify-center rounded-full bg-saffron/20 ring-8 ring-saffron/10' aria-hidden='true'>
                        <Sticker className='size-12 text-saffron-strong' />
                    </span>
                ) : (
                    <span className='flex size-16 items-center justify-center rounded-full bg-success-soft' aria-hidden='true'>
                        <Trophy className='size-8 text-success' />
                    </span>
                )}
                <h2 id='summary-heading' className='flex flex-col gap-1'>
                    <span lang='hi' className='text-2xl font-bold sm:text-3xl'>{kids ? 'खेल पूरा!' : 'सत्र पूरा'}</span>
                    <span lang='en' className='font-sans text-base font-medium text-muted-foreground'>{kids ? 'Game over, well played!' : 'Session complete'}</span>
                </h2>
                <p className='max-w-md'>
                    {kids ? <Bi hi={kidsCheer.text.hi} en={kidsCheer.text.en} /> : <Bi hi={adultsCheer.text.hi} en={adultsCheer.text.en} />}
                </p>
                {kids && correct > 0 && (
                    <p className='flex flex-wrap justify-center gap-1' aria-label={`${correct} सितारे · ${correct} stars`}>
                        {Array.from({ length: correct }, (_, i) => (
                            <Star key={i} className='size-7 fill-saffron text-saffron-strong' aria-hidden='true' />
                        ))}
                    </p>
                )}
            </header>

            <div className='grid grid-cols-3 gap-2 sm:gap-3'>
                <Stat label={{ hi: 'सही उत्तर', en: 'Correct' }} value={`${correct}/${total}`} icon={null} />
                {kids
                    ? <Stat label={{ hi: 'सितारे', en: 'Stars' }} value={correct} icon={<Star className='size-5 fill-saffron text-saffron-strong' aria-hidden='true' />} />
                    : <Stat label={{ hi: 'अंक', en: 'XP' }} value={`+${xp}`} icon={<Sparkles className='size-5 text-saffron-strong' aria-hidden='true' />} />}
                <Stat label={{ hi: 'सबसे लम्बी लड़ी', en: 'Best streak' }} value={bestStreak} icon={<Flame className='size-5 text-terracotta' aria-hidden='true' />} />
            </div>

            {!kids && <XpAccountNote />}

            {misses.length > 0 && (
                <section aria-labelledby='review-heading' className='flex flex-col gap-3'>
                    <h3 id='review-heading' className='text-lg font-semibold'>
                        <Bi hi='इन्हें दोहराइए' en='What to review' layout='inline' always />
                    </h3>
                    <ul className='flex flex-col gap-2'>
                        {misses.map(p => (
                            <li key={p.index} className='rounded-xl border border-border bg-card px-4 py-3 shadow-sm'>
                                <p className='mb-1 flex flex-wrap items-baseline gap-x-2 text-xs font-semibold text-muted-foreground'>
                                    <span>{p.index + 1}.</span>
                                    <span lang='hi' className='hindi'>{modeInfo(p.question.mode).name.hi}</span>
                                    {p.result.timedOut && (
                                        <span className='text-danger'>
                                            <span lang='hi' className='hindi'>समय पूरा</span> · <span lang='en'>time up</span>
                                        </span>
                                    )}
                                    {p.result.vikalp && p.result.vikalp.length > 0 && <StatusBadge kind='alt' className='self-center' />}
                                </p>
                                {p.result.expected && (
                                    <p className={cn(/[ऀ-ॿ]/.test(p.result.expected) && !p.result.expected.includes(' · ') ? 'pra pra-md' : 'font-medium')}>
                                        <span lang={/[ऀ-ॿ]/.test(p.result.expected) && !p.result.expected.includes(' · ') ? 'pra' : undefined}>{p.result.expected}</span>
                                    </p>
                                )}
                                {p.result.expectedMeaning && (
                                    <Bi hi={p.result.expectedMeaning.hi} en={p.result.expectedMeaning.en} layout='inline' className='text-sm' enClassName='text-muted-foreground' />
                                )}
                                <p className='mt-1 text-sm text-muted-foreground'>
                                    <Bi hi={p.result.feedback.hi} en={p.result.feedback.en} />
                                </p>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {drills.length > 0 && (
                <section aria-labelledby='drill-heading' className='flex flex-col gap-2'>
                    <h3 id='drill-heading' className='text-lg font-semibold'>
                        <Bi hi='इस पर और अभ्यास' en='Drill this next' layout='inline' always />
                    </h3>
                    <ul className='grid gap-2 sm:grid-cols-2'>
                        {drills.map(d => (
                            <li key={d.code}>
                                <Link href={`${d.href}?level=${level}`} className='flex min-h-tap items-center gap-3 rounded-xl border border-terracotta/30 bg-card px-4 py-3 hover:bg-terracotta/5'>
                                    <Dumbbell className='size-5 shrink-0 text-terracotta' aria-hidden='true' />
                                    <Bi hi={d.hi} en={d.en} className='flex-1 text-sm' />
                                    <ArrowRight className='size-4 shrink-0 text-terracotta' aria-hidden='true' />
                                </Link>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {!signedIn && (
                <p className='rounded-xl bg-surface-muted px-4 py-3 text-sm'>
                    <Bi
                        hi={kids ? 'तुम्हारे सितारे इसी डिवाइस पर सहेजे गए हैं।' : 'आपके अंक इसी डिवाइस पर सहेजे गए हैं।'}
                        en={kids ? 'Your stars are saved on this device.' : 'Your XP is saved on this device.'}
                    />
                    <Link href='/account' className='mt-1 inline-flex items-center gap-1.5 font-medium text-saffron-strong underline underline-offset-4'>
                        <UserPlus className='size-4' aria-hidden='true' />
                        <Bi hi='खाता बनाइए — हर डिवाइस पर प्रगति साथ रहेगी' en='Sign up to keep your progress on every device' layout='inline' always />
                    </Link>
                </p>
            )}

            <div className='flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-center'>
                <button
                    type='button'
                    onClick={onReplay}
                    className='inline-flex min-h-tap items-center justify-center gap-2 rounded-full bg-primary px-6 py-2.5 font-semibold text-primary-foreground shadow-card hover:bg-primary/85'
                >
                    <RotateCcw className='size-4' aria-hidden='true' />
                    <Bi hi='फिर से खेलें' en='Play again' layout='inline' always />
                </button>
                {mode !== 'aaj' && set && set.set < set.sets && (
                    <Link
                        href={`${practiceHref(mode, level, topics, vikalp)}&set=${set.set + 1}`}
                        className='inline-flex min-h-tap items-center justify-center gap-2 rounded-full border border-saffron bg-saffron/10 px-6 py-2.5 font-semibold text-saffron-strong hover:bg-saffron/15'
                    >
                        <ArrowRight className='size-4' aria-hidden='true' />
                        <Bi hi={`अगला सेट: ${set.set + 1}`} en={`Next set: ${set.set + 1}`} layout='inline' always />
                    </Link>
                )}
                {mode !== 'aaj' && set && (
                    <Link
                        href={practiceHref(mode, level, topics, vikalp)}
                        className='inline-flex min-h-tap items-center justify-center gap-2 rounded-full border border-border bg-card px-6 py-2.5 font-semibold hover:border-saffron/60 hover:bg-saffron/5'
                    >
                        <LayoutGrid className='size-4 text-saffron-strong' aria-hidden='true' />
                        <Bi hi={`सभी ${set.sets} सेट`} en={`All ${set.sets} sets`} layout='inline' always />
                    </Link>
                )}
                {mode !== 'aaj' && !set && nextLevel && (
                    <Link
                        href={practiceHref(mode, nextLevel, topics, vikalp)}
                        className='inline-flex min-h-tap items-center justify-center gap-2 rounded-full border border-border bg-card px-6 py-2.5 font-semibold hover:border-saffron/60 hover:bg-saffron/5'
                    >
                        <ArrowRight className='size-4 text-saffron-strong' aria-hidden='true' />
                        <Bi hi={`अगला स्तर: ${LEVEL_INFO[nextLevel].name.hi}`} en={`Next level: ${LEVEL_INFO[nextLevel].name.en}`} layout='inline' always />
                    </Link>
                )}
                <Link
                    href={hubHref}
                    className='inline-flex min-h-tap items-center justify-center gap-2 rounded-full border border-border bg-card px-6 py-2.5 font-semibold hover:border-saffron/60 hover:bg-saffron/5'
                >
                    <Dumbbell className='size-4 text-saffron-strong' aria-hidden='true' />
                    <Bi hi='सभी खेल' en='All games' layout='inline' always />
                </Link>
                <Link
                    href='/progress'
                    className='inline-flex min-h-tap items-center justify-center gap-2 rounded-full border border-border bg-card px-6 py-2.5 font-semibold hover:border-saffron/60 hover:bg-saffron/5'
                >
                    <TrendingUp className='size-4 text-saffron-strong' aria-hidden='true' />
                    <Bi hi='मेरी प्रगति' en='My progress' layout='inline' always />
                </Link>
            </div>
        </section>
    )
}
